import React, { useState } from 'react';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PostType } from './main';
import { MoreVertical } from "lucide-react";
import { useCollapse } from 'react-collapsed';
import Microlink from "@microlink/react";
import './post.css';

interface Props {
    post: PostType;
    onPostDeleted?: (postId: string) => void;
    userRole: UserRole | null;
    currentUser: any;
}

interface UserRole {
    isAdmin: boolean;
    canManageCourses: boolean;
    canViewPosts: boolean;
    canCreatePosts: boolean;
    canManageUnits: boolean;
}

export const Post = ({ post, onPostDeleted, userRole, currentUser }: Props) => {
    const [websiteEmbedError, setWebsiteEmbedError] = useState(false);
    const [pdfEmbedError, setPdfEmbedError] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const { getCollapseProps, getToggleProps, isExpanded } = useCollapse();

    // Permission check function
    const canUserModifyPost = () => {
        // Admin can modify any post
        if (userRole?.isAdmin) return true;
        
        // Course managers can modify any post
        if (userRole?.canManageCourses) return true;
        
        // Users can only modify their own posts
        if (post.userId === currentUser?.uid) return true;
        
        return false;
    };

    // Delete function
    const deletePost = async () => {
        if (!canUserModifyPost()) {
            alert("You don't have permission to delete this post.");
            return;
        }
        
        if (!window.confirm("Are you sure you want to delete this post?")) return;
        
        try {
            await deleteDoc(doc(db, "posts", post.id));
            
            // Call parent callback to update state
            if (onPostDeleted) {
                onPostDeleted(post.id);
            }
            
            alert("Post deleted successfully!");
        } catch (error) {
            console.error("Error deleting post:", error);
            alert("Error deleting post");
        }
    };

    const handleWebsiteEmbedError = () => {
        setWebsiteEmbedError(true);
    };

    const handlePdfEmbedError = () => {
        setPdfEmbedError(true);
    };

    const isValidUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const toggleDropdown = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent post from expanding/collapsing
        setShowDropdown(!showDropdown);
    };

    return (
        <div className={`post-container border border-gray-200 rounded-xl overflow-hidden ${isExpanded ? '' : 'hover:bg-gray-200'}`}>
            <div 
                className="title p-4 bg-white cursor-pointer hover:bg-gray-200"
                {...getToggleProps()}
            >
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-black">{post.title}</h2>
                    
                    {canUserModifyPost() && (
                        <div style={{ position: 'relative' }}>
                            <button 
                            className="p-1.5 hover:bg-gray-300 rounded-full cursor-pointer transition-colors bg-transparent border-none"
                            onClick={toggleDropdown}
                        >
                            <MoreVertical size={16} className="text-black-500"/>
                            </button>

                            {showDropdown && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: '0',
                                    backgroundColor: 'rgba(0,0,0,0.95)',
                                    border: '1px solid #555',
                                    borderRadius: '8px',
                                    minWidth: '160px',
                                    zIndex: 1000,
                                    marginTop: '4px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                                }}>
                                  
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deletePost();
                                            setShowDropdown(false);
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '12px 16px',
                                            backgroundColor: 'transparent',
                                            color: '#dc3545',
                                            border: 'none',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                    >
                                        Delete Post
                                    </button>

                                    
                                    <div style={{ 
                                        padding: '8px 16px', 
                                        borderTop: '1px solid #555',
                                        fontSize: '11px',
                                        color: '#888'
                                    }}>
                                        {userRole?.isAdmin && "Admin Access"}
                                        {userRole?.canManageCourses && !userRole?.isAdmin && "Course Manager"}
                                        {post.userId === currentUser?.uid && !userRole?.isAdmin && !userRole?.canManageCourses && "Your Post"}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <p className="text-sm text-gray-600">{post.selectedCourseOption} | {post.selectedUnitOption}</p>
            </div>

            <div {...getCollapseProps()}>
                <div className="expanded-content p-4 bg-darkred">
                    <hr className="my-2" />
                    <p className="text-sm text-gray-600 text-left ml-12">Posted by: {post.username} | {post.selectedSchoolOption}</p>
                    <div className="post-body mt-4 text-black">
                        <p>{post.description}</p>
                    </div>
                <div className="flex flex-wrap gap-4">
                    {post.hyperlink && post.hyperlink.trim() !== "" && (
                        <div className="websitehyperlink mt-4">
                            {!websiteEmbedError && isValidUrl(post.hyperlink) && (
                                <Microlink 
                                    url={post.hyperlink} 
                                    onError={handleWebsiteEmbedError} 
                                    className="hover:shadow-lg transition-shadow duration-300 w-64 h-32 ml-12 bg-[#181818] rounded-xl"
                                />
                            )}
                            <p>
                                <a href={post.hyperlink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    Hyperlink
                                </a>
                            </p>
                        </div>
                    )}

                    {post.pdflink && post.pdflink.trim() !== "" && (
                        <div className="pdflink mt-4">
                            {!pdfEmbedError && isValidUrl(post.pdflink) && (
                                <Microlink url={post.pdflink} onError={handlePdfEmbedError} className="hover:shadow-lg transition-shadow duration-300 w-64 h-32 ml-12 rounded-xl" />
                            )}
                            <p>
                                <a href={post.pdflink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    PDF Link
                                </a>
                            </p>
                        </div>
                    )}
                </div>
                </div>
            </div>
        </div>
    );
};

